// src/pages/Home.jsx
import React, { useMemo, useState } from "react";
import HeroSection from "../components/HeroSection";
import GalleryGrid from "../components/GalleryGrid";
import PhotoModal from "../components/PhotoModal";
import photoList from "../data/photos";

// --- deterministic shuffle helpers (fixed "random" order) ---
const hashString = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (seed) => {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleWithSeed = (arr, seed) => {
  const a = [...arr];
  const rand = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.pageYOffset - 16;
  window.scrollTo({ top: y, behavior: "smooth" });
};

const Home = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  // null | "signature" | "lifestyle"
  const [activeGallery, setActiveGallery] = useState(null);

  const { signature, lifestyle } = useMemo(() => {
    const golden = photoList.filter((p) => p.collection === "golden_hour");
    const other = photoList.filter((p) => p.collection !== "golden_hour");

    const GOLDEN_SEED = "eli-signature-v1";
    const shuffledGolden = shuffleWithSeed(golden, hashString(GOLDEN_SEED));

    return { signature: shuffledGolden, lifestyle: other };
  }, []);

  // Previews pulled from the actual collections (first image)
  const signaturePreview = "/photos/golden_hour/photo36.jpg";
  const lifestylePreview = "/photos/photos/photo2.jpg";

  const openGallery = (which) => {
    setActiveGallery(which);
    setTimeout(() => scrollToId("gallery-view"), 0);
  };

  const closeGallery = () => {
    setActiveGallery(null);
    setSelectedPhoto(null);
    setTimeout(() => scrollToId("gallery-chooser"), 0);
  };

  const Card = ({
  tone = "primary",
  title,
  subtitle,
  description,
  previewSrc,
  onClick,
  photoFocus = "50% 50%",
}) => {
  const isPrimary = tone === "primary";

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "group relative w-full overflow-hidden rounded-2xl text-left",
        "border transition",
        "shadow-[0_18px_70px_rgba(0,0,0,0.35)]",
        "focus:outline-none focus:ring-2 focus:ring-white/25",
        isPrimary
          ? "border-white/20 bg-white/[0.04] hover:bg-white/[0.06]"
          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]",
      ].join(" ")}
    >
      {/* Preview image */}
      <div
        className={[
          "relative overflow-hidden",
          // Taller at md (iPad), only go wide on lg+
          "aspect-[4/3] sm:aspect-[16/10] md:aspect-[16/11] lg:aspect-[2.35/1] xl:aspect-[2.6/1]",
          // Safety net: prevent “too short” cards on weird tablet sizes
          "min-h-[210px] sm:min-h-[230px] md:min-h-[250px] lg:min-h-0",
        ].join(" ")}
      >
        {/* subtle inner rim */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 z-20" />

        {previewSrc ? (
          <div
            className="absolute inset-0 bg-cover transition-all duration-700 group-hover:scale-[1.03] brightness-[1.07] contrast-[1.02] group-hover:brightness-[1.12]"
            style={{
              backgroundImage: `url(${previewSrc})`,
              backgroundPosition: photoFocus,
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.07),rgba(0,0,0,0.85)_70%)]" />
        )}

        {/* Sleek overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/34 to-black/18 transition-all duration-500 group-hover:from-black/45 group-hover:via-black/26 group-hover:to-black/12" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-90" />
        <div className="absolute inset-0 bg-black/10" />

        {/* Content */}
        <div className="relative z-30 h-full p-4 sm:p-6 flex flex-col justify-end">
          <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-white/70">
            {subtitle}
          </p>

          <div className="mt-2 flex items-center gap-3 min-w-0">
            <h3
              className={[
                "min-w-0 font-serif text-white leading-tight",
                // Don’t jump too big on iPad
                "text-lg sm:text-xl md:text-2xl lg:text-3xl",
                // Prevent overflow/cutoff
                "line-clamp-2",
                "drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]",
              ].join(" ")}
            >
              {title}
            </h3>

            {isPrimary && (
              <span className="hidden sm:inline-flex shrink-0 items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] tracking-[0.22em] uppercase text-white/80">
                Primary
              </span>
            )}
          </div>

          <p className="mt-2 text-xs sm:text-sm text-white/70 leading-relaxed max-w-[34rem] line-clamp-3">
            {description}
          </p>

          {/* CTA */}
          <div className="mt-4 sm:mt-6 inline-flex items-center gap-3 text-xs sm:text-sm text-white/85">
            <span className="tracking-[0.20em] uppercase font-light">Open</span>
            <span
              className={[
                "h-px bg-white/30 transition-all duration-300",
                isPrimary ? "w-12 group-hover:w-16" : "w-10 group-hover:w-14",
              ].join(" ")}
            />
          </div>
        </div>
      </div>

      {/* bottom accent */}
      <div
        className={[
          "h-[2px] w-full",
          isPrimary
            ? "bg-gradient-to-r from-white/0 via-white/35 to-white/0 opacity-80"
            : "bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-60",
        ].join(" ")}
      />
    </button>
  );
};

  return (
    <div className="bg-[#121316] text-[#f0eae2] min-h-screen overflow-x-hidden">
      <HeroSection title="The Art of Stillness" subtitle="Capturing Moments" />

      {/* more intentional mobile spacing */}
      <main className="px-4 sm:px-6 lg:px-10 pt-4 pb-12 space-y-10 md:space-y-12 max-w-[1800px] mx-auto">
        {/* CHOOSER */}
        {activeGallery === null && (
          <section id="gallery-chooser" className="text-center">
            {/* tighter + more modern container */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] shadow-[0_25px_90px_rgba(0,0,0,0.55)] backdrop-blur-xl px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10">
              {/* Smaller on mobile, still elegant */}
              <h2 className="text-xl sm:text-2xl md:text-3xl font-serif tracking-tight text-white/95">
                Choose a Gallery
              </h2>

              {/* Cards stack cleanly on mobile */}
              <div className="mt-6 sm:mt-8 grid gap-4 sm:gap-6 md:grid-cols-2">
                <Card
                  tone="primary"
                  title="Weddings & Couples"
                  subtitle="Signature"
                  description="Engagements, weddings, and quiet in-between moments—our main focus."
                  previewSrc={signaturePreview}
                  photoFocus="40% 35%"
                  onClick={() => openGallery("signature")}
                />

                <Card
                  tone="secondary"
                  title="Lifestyle & Events"
                  subtitle="Collection"
                  description="Portraits, lifestyle sessions, and select event coverage."
                  previewSrc={lifestylePreview}
                  photoFocus="50% 30%"
                  onClick={() => openGallery("lifestyle")}
                />
              </div>
            </div>
          </section>
        )}

        {/* ACTIVE GALLERY ONLY */}
        {activeGallery === "signature" && (
          <section
            id="gallery-view"
            className="bg-[#1a1b1e] rounded-xl p-4 sm:p-6 md:p-8 shadow-md border border-[#2a2b2e]"
          >
            <div className="mb-4 sm:mb-6 text-center">
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-white/55">
                Signature
              </p>
              <h2 className="text-2xl sm:text-3xl font-serif border-b border-[#cccccc]/50 inline-block pb-1">
                Weddings & Couples
              </h2>

              <div className="mt-5 flex items-center justify-center">
                <button
                  type="button"
                  onClick={closeGallery}
                  className="rounded-full border border-white/15 bg-white/10 px-5 py-2 text-xs tracking-[0.22em] uppercase text-white/80
                             backdrop-blur-md hover:bg-white/14 hover:border-white/25 transition
                             focus:outline-none focus:ring-2 focus:ring-white/25"
                >
                  Close
                </button>
              </div>
            </div>

            <GalleryGrid
              photos={signature}
              onPhotoClick={setSelectedPhoto}
              layout="clean"
            />
          </section>
        )}

        {activeGallery === "lifestyle" && (
          <section
            id="gallery-view"
            className="bg-[#1a1b1e] rounded-xl p-4 sm:p-6 md:p-8 shadow-md border border-[#2a2b2e]"
          >
            <div className="mb-4 sm:mb-6 text-center">
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-white/55">
                Collection
              </p>
              <h2 className="text-2xl sm:text-3xl font-serif border-b border-[#cccccc]/50 inline-block pb-1">
                Lifestyle & Events
              </h2>

              <div className="mt-5 flex items-center justify-center">
                <button
                  type="button"
                  onClick={closeGallery}
                  className="rounded-full border border-white/15 bg-white/10 px-5 py-2 text-xs tracking-[0.22em] uppercase text-white/80
                             backdrop-blur-md hover:bg-white/14 hover:border-white/25 transition
                             focus:outline-none focus:ring-2 focus:ring-white/25"
                >
                  Close
                </button>
              </div>
            </div>

            <GalleryGrid
              photos={lifestyle}
              onPhotoClick={setSelectedPhoto}
              layout="clean"
            />
          </section>
        )}
      </main>

      {selectedPhoto && (
        <PhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
      )}
    </div>
  );
};

export default Home;